import { useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { orderService } from '../services/orderService';
import { OrderList } from './OrderList';

export const OrderManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    data: ordersResponse,
    isLoading: isLoadingOrders,
    error: ordersError,
    refetch: refetchOrders,
  } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.getOrders(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const orders = ordersResponse || [];

  if (isLoadingOrders) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-2">
        <div className="flex flex-col items-center gap-4 text-text-secondary">
          <Spinner className="size-10" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (ordersError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg bg-surface-card p-8 text-danger-600 shadow-sm">
        <p>
          Error loading data: {ordersError instanceof Error ? ordersError.message : 'Unknown error'}
        </p>
        <Button type="button" onClick={() => void refetchOrders()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <OrderList
        orders={orders}
        isLoading={isLoadingOrders}
        onEdit={() => {
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }}
        onDelete={async (orderId) => {
          if (
            window.confirm(
              'Are you sure you want to delete this order? This action cannot be undone.'
            )
          ) {
            try {
              await orderService.deleteOrder(orderId);
              queryClient.invalidateQueries({ queryKey: ['orders'] });
            } catch (error) {
              alert(
                `Failed to delete order: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          }
        }}
        onAdd={() => {
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        }}
      />
    </div>
  );
};
